import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Gift, Copy, CheckCircle2, Clock, Users, Coins } from 'lucide-react';
import { Database } from '../types/database.types';

type Referral = Database['public']['Tables']['referrals']['Row'];
type ReferralSettings = Database['public']['Tables']['referral_settings']['Row'];

function generateCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = 'SEU-';
  for (let i = 0; i < 5; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export default function ReferralPanel() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [settings, setSettings] = useState<ReferralSettings | null>(null);
  const [myCode, setMyCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    async function fetchData() {
      setLoading(true);
      // Fetch settings
      const { data: settingsData } = await supabase.from('referral_settings').select('*').eq('id', 'default').single();
      setSettings(settingsData);

      // Fetch user's referrals
      const { data: refs } = await supabase.from('referrals').select('*').eq('referrer_id', user!.id).order('created_at', { ascending: false });
      setReferrals(refs || []);

      // Find or create referral code
      if (refs && refs.length > 0) {
        setMyCode(refs[0].referral_code);
      } else {
        // Create a new referral code entry
        const code = generateCode();
        const { error } = await supabase.from('referrals').insert({
          referrer_id: user!.id,
          referral_code: code,
          status: 'pending',
        });
        if (!error) {
          setMyCode(code);
          setReferrals([{ id: '', referrer_id: user!.id, referral_code: code, status: 'pending', created_at: new Date().toISOString(), completed_at: null, referred_email: null, referred_user_id: null }]);
        }
      }
      setLoading(false);
    }
    fetchData();
  }, [user]);

  const referralUrl = `${window.location.origin}/register?ref=${myCode || ''}`;
  const completedCount = referrals.filter(r => r.status === 'completed').length;
  const earnedCredits = completedCount * (settings?.bonus_credits_referrer ?? 5);

  const handleCopy = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="glass-card wide-card" style={{ textAlign: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Painel de Indicações</h1>
          <p className="subtitle">Indique novos usuários e ganhe créditos bônus</p>
        </div>
      </div>

      {settings && !settings.is_active && (
        <div className="alert-banner alert-warning">
          <Gift size={18} />
          <span>O programa de indicações está temporariamente inativo.</span>
        </div>
      )}

      {/* Referral Link Card */}
      <div className="glass-card wide-card referral-link-card">
        <div className="referral-code-section">
          <h2><Gift size={24} /> Seu Código de Indicação</h2>
          <div className="referral-code-display">
            <code>{myCode || '...'}</code>
          </div>
          <div className="referral-url-bar">
            <input type="text" readOnly value={referralUrl} className="referral-url-input" />
            <button className="btn-primary" style={{ width: 'auto', whiteSpace: 'nowrap' }} onClick={handleCopy}>
              {copied ? <><CheckCircle2 size={16} /> Copiado!</> : <><Copy size={16} /> Copiar Link</>}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Users size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Convites Criados</span>
            <span className="stat-value">{referrals.length}</span>
          </div>
        </div>
        <div className="stat-card stat-green">
          <div className="stat-icon"><CheckCircle2 size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Conversões</span>
            <span className="stat-value">{completedCount}</span>
          </div>
        </div>
        <div className="stat-card stat-orange">
          <div className="stat-icon"><Coins size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Créditos Ganhos</span>
            <span className="stat-value">{earnedCredits}</span>
          </div>
        </div>
      </div>

      {/* Settings Info */}
      {settings && (
        <div className="glass-card wide-card" style={{ marginBottom: '1.5rem' }}>
          <h2>Regras do Programa</h2>
          <div className="rules-grid">
            <div className="rule-item">
              <span className="rule-label">Você ganha</span>
              <span className="rule-value">{settings.bonus_credits_referrer} créditos</span>
            </div>
            <div className="rule-item">
              <span className="rule-label">Indicado ganha</span>
              <span className="rule-value">{settings.bonus_credits_referred} créditos</span>
            </div>
            <div className="rule-item">
              <span className="rule-label">Máx. indicações</span>
              <span className="rule-value">{settings.max_referrals_per_user ?? 'Ilimitado'}</span>
            </div>
            <div className="rule-item">
              <span className="rule-label">Validade do convite</span>
              <span className="rule-value">{settings.referral_expiry_days ?? 30} dias</span>
            </div>
          </div>
        </div>
      )}

      {/* Referrals List */}
      <div className="glass-card wide-card">
        <h2>Histórico de Indicações</h2>
        {referrals.length === 0 ? (
          <div className="empty-state">
            <Gift size={40} />
            <p>Nenhuma indicação ainda. Compartilhe seu link!</p>
          </div>
        ) : (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr><th>Código</th><th>Email</th><th>Status</th><th>Data</th></tr>
              </thead>
              <tbody>
                {referrals.map(r => (
                  <tr key={r.id || r.referral_code}>
                    <td><code className="code-inline">{r.referral_code}</code></td>
                    <td className="text-muted">{r.referred_email || '—'}</td>
                    <td>
                      <span className={`badge ${r.status === 'completed' ? 'badge-success' : r.status === 'expired' ? 'badge-error' : 'badge-warning'}`}>
                        {r.status === 'completed' ? <><CheckCircle2 size={12} /> Convertido</> : r.status === 'expired' ? 'Expirado' : <><Clock size={12} /> Pendente</>}
                      </span>
                    </td>
                    <td className="text-muted">{r.created_at ? new Date(r.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
